import { useId } from "react";

import { cn } from "@/lib/utils";

/**
 * Compact inline trend chart — a single series rendered as a smoothed line
 * with a soft gradient fill. Pure SVG, no chart library. Scales to its
 * container via a viewBox, so it stays crisp at any size.
 *
 * Intended for real series data only (e.g. weekly pipeline values). Render
 * nothing when there isn't enough data to draw a trend.
 */

interface SparklineProps {
  /** Ordered data points, oldest → newest. Needs at least 2 to draw. */
  data: number[];
  /** Stroke + fill colour. Any CSS colour; defaults to the primary accent. */
  color?: string;
  className?: string;
  /** Internal coordinate space — controls aspect ratio + smoothing scale. */
  width?: number;
  height?: number;
  /** Draw the gradient area beneath the line. */
  fill?: boolean;
  /** Draw the dot at the newest point. Defaults true. */
  showEndDot?: boolean;
  /** Optional label when the tiny chart needs to be announced. */
  ariaLabel?: string;
}

export function Sparkline({
  data,
  color = "hsl(var(--primary))",
  className,
  width = 120,
  height = 36,
  fill = true,
  showEndDot = true,
  ariaLabel,
}: SparklineProps) {
  const gradientId = useId();

  if (!data || data.length < 2) return null;

  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = max - min || 1;
  const pad = 3;
  const innerW = width - pad * 2;
  const innerH = height - pad * 2;

  const points = data.map((value, i) => {
    const x = pad + (i / (data.length - 1)) * innerW;
    const y = pad + innerH - ((value - min) / span) * innerH;
    return [x, y] as const;
  });

  // Catmull-Rom → Bézier for a gently smoothed line.
  let line = `M ${points[0][0]} ${points[0][1]}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] ?? points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] ?? p2;
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    line += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2[0]} ${p2[1]}`;
  }

  const area = `${line} L ${points[points.length - 1][0]} ${height} L ${points[0][0]} ${height} Z`;
  const last = points[points.length - 1];

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className={cn("overflow-visible", className)}
      role={ariaLabel ? "img" : undefined}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.28} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      {fill && <path d={area} fill={`url(#${gradientId})`} stroke="none" />}
      <path
        d={line}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      {showEndDot && <circle cx={last[0]} cy={last[1]} r={2.6} fill={color} />}
    </svg>
  );
}
